import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, Link, Button, Drawer, Typography, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
// hooks
import useAuth from '../../hooks/useAuth';
import usePermissions from '../../hooks/usePermissions';
// routes
import { PATH_DASHBOARD, PATH_DOCS } from '../../routes/paths';
// components
import Logo from '../../components/Logo';
import MyAvatar from '../../components/MyAvatar';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
import CustomTooltip from '../../components/CustomTooltip';
//
import sidebarConfig from './SidebarConfig';
import { FACILITIES } from './FacilitySelector';
import { DocIcon } from '../../assets';
import { filterSidebarForFacility } from '../../utils/facilityMode';
// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 88;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;
const DARK_MODE_GREEN = '#85e966';

const drawerPaperSx = (isOpenSidebar) => ({
  width: isOpenSidebar ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
  bgcolor: 'background.default',
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
});

const SidebarHeaderStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDarkModeFacility' && prop !== 'isOpenSidebar'
})(({ theme, isDarkModeFacility, isOpenSidebar }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  minHeight: APPBAR_MOBILE,
  height: APPBAR_MOBILE,
  paddingLeft: theme.spacing(isOpenSidebar ? 2 : 1),
  paddingRight: theme.spacing(isOpenSidebar ? 2 : 1),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    height: APPBAR_DESKTOP
  },
  ...(isDarkModeFacility && {
    backgroundColor: DARK_MODE_GREEN,
    boxShadow: `1px 0 0 ${DARK_MODE_GREEN}`,
    zIndex: 1
  })
}));

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpenSidebar'
})(({ theme, isOpenSidebar }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: isOpenSidebar ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
  }
}));

const AccountStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpenSidebar'
})(({ theme, isOpenSidebar }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isOpenSidebar ? 'flex-start' : 'center',
  padding: isOpenSidebar ? theme.spacing(2, 2.5) : theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

const DocStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor:
    theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.08) : theme.palette.primary.lighter
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user, allowedModules, selectedFacility } = useAuth();
  const { hasPermission } = usePermissions();

  const selectedFacilityObj = FACILITIES.find(f => f.id === selectedFacility);
  const isDarkModeFacility = selectedFacilityObj?.name?.toLowerCase().includes('dark mode') || false;

  useEffect(() => {
    // On mobile, close sidebar when path changes
    if (isOpenSidebar && window.innerWidth < 1200) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const isLongName = name.length > 12;
  const truncatedName = isLongName ? `${name.substring(0, 12)}...` : name;

  const handleToggle = isOpenSidebar ? onCloseSidebar : onOpenSidebar;

  const renderSidebarHeader = (
    <SidebarHeaderStyle isDarkModeFacility={isDarkModeFacility} isOpenSidebar={isOpenSidebar}>
      {isOpenSidebar && (
        <Box
          component={RouterLink}
          to="/dashboard/maindashboard"
          sx={{ display: 'inline-flex', transform: isDarkModeFacility ? 'scale(1.2)' : 'scale(2.2)' }}
        >
          <Logo darkMode={isDarkModeFacility} sx={isDarkModeFacility ? { width: 32, height: 32 } : undefined} />
        </Box>
      )}
      <IconButton
        onClick={handleToggle}
        sx={{
          transition: 'none',
          ...(isOpenSidebar && {
            position: 'absolute',
            top: '50%',
            right: 12,
            transform: 'translateY(-50%)'
          }),
          ...(isDarkModeFacility
            ? {
                width: 40,
                height: 40,
                bgcolor: DARK_MODE_GREEN,
                color: 'common.black',
                borderRadius: 1
              }
            : {
                color: 'text.secondary'
              })
        }}
      >
        {isOpenSidebar ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </IconButton>
    </SidebarHeaderStyle>
  );

  const renderScrollContent = (
    <Scrollbar
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ mt: 2, mb: 2, mx: isOpenSidebar ? 2.5 : 1.5 }}>
        <Link underline="none" component={RouterLink} to={PATH_DASHBOARD.user.account}>
          <AccountStyle isOpenSidebar={isOpenSidebar}>
            <MyAvatar />
            {isOpenSidebar && (
              <Box sx={{ ml: 2 }}>
                {isLongName ? (
                  <CustomTooltip title={name}>
                    <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                      {truncatedName}
                    </Typography>
                  </CustomTooltip>
                ) : (
                  <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    {truncatedName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user.role}
                </Typography>
              </Box>
            )}
          </AccountStyle>
        </Link>
      </Box>

      <NavSection 
        isOpenSidebar={isOpenSidebar}
        onOpenSidebar={onOpenSidebar}
        navConfig={filterSidebarForFacility(
          sidebarConfig.map(group => ({
            ...group,
            items: group.items.filter(item => {
              if (item.title === 'dashboard') return true;
              
              // Check allowedModules for parent (Facility-level control remains)
              if (item.moduleId && !allowedModules.includes(item.moduleId)) return false;

              return true;
            })
          })),
          selectedFacility
        )} 
      />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        {/* <DocStyle>
          <DocIcon sx={{ width: 36, height: 36, mb: 2 }} />
          <Typography gutterBottom variant="subtitle1" sx={{ color: 'grey.800' }}>
            Hi, {user.displayName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Need help?
            <br /> Please check our docs
          </Typography>
          <Button fullWidth to={PATH_DOCS.root} variant="contained" component={RouterLink}>
            Documentation
          </Button>
        </DocStyle> */}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle isOpenSidebar={isOpenSidebar}>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          {renderSidebarHeader}
          {renderScrollContent}
        </Drawer>
      </MHidden>
      
      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: drawerPaperSx(isOpenSidebar)
          }}
        >
          {renderSidebarHeader}
          {renderScrollContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}